import { Fragment } from 'react';
import { Plus, RefreshCw, X } from 'lucide-react';
import type { PackBucketRow, PackBucketsBucket } from '../../lib/api/operatorConsole';
import { Button } from '../../components/ui/Button';
import { NumberCell } from '../MyPacks/odds/NumberCell';
import { formatPercent } from '../MyPacks/packBuilderFormat';
import { BucketShareBars } from './BucketShareBars';
import { formatDollars, formatMoney } from './pricing';

type Preview = {
  /** The dry run of the draft on screen; null until it answers. */
  buckets: PackBucketsBucket[] | null;
  yourShare: boolean;
};

type Props = {
  groups: PackBucketRow[];
  price: number;
  disabled: boolean;
  onChange: (groups: PackBucketRow[]) => void;
  onAddRow?: () => void;
  onRemoveRow?: (index: number) => void;
  onSuggest?: () => void;
  /** The dry-run columns; a read-only table has none. */
  preview?: Preview;
};

const INTRO =
  'Card value ranges the odds are solved over. One boundary must equal the whole-number price. ' +
  'Min % is the smallest share of pulls a bucket must get; leave it empty to let the solver decide.';

const head = 'px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]';
const cell = 'px-3 py-1.5 text-[12px] text-[var(--co-text)]';
const figure = `${cell} text-right font-mono`;
const headerButton = 'gap-2 px-3 py-1 text-[12px]';

/**
 * The pack's ladder, one row per bucket. A bucket ends where the next one starts, so only the
 * last row's top is typed in; moving a row's start moves the row above's top with it.
 */
export function BucketsTable({ groups, price, disabled, onChange, onAddRow, onRemoveRow, onSuggest, preview }: Props) {
  const last = groups.length - 1;
  const priceEdge = Math.floor(price);
  const hasPriceEdge = groups.some((g) => g.from === priceEdge);
  const floorsTotal = groups.reduce((sum, g) => sum + (g.floor ?? 0), 0);
  const floorsOverbooked = floorsTotal >= 100;
  const buckets = preview?.buckets ?? null;
  const firstHigh = buckets?.findIndex((b) => b.side === 'high') ?? -1;
  const columns = 3 + (preview ? (preview.yourShare ? 3 : 2) : 0) + (onRemoveRow ? 1 : 0);

  function updateFrom(index: number, value: number | null) {
    if (value === null) return;
    const next = groups.map((g, i) => (i === index ? { ...g, from: value } : g));
    const previous = next[index - 1];
    if (previous) next[index - 1] = { ...previous, to: value };
    onChange(next);
  }

  function updateLastTo(value: number | null) {
    onChange(groups.map((g, i) => (i === last ? { ...g, to: value } : g)));
  }

  function updateFloor(index: number, value: number | null) {
    onChange(groups.map((g, i) => (i === index ? { ...g, floor: value } : g)));
  }

  return (
    <section className="rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)]">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--co-border)] px-4 py-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-[13px] font-semibold text-[var(--co-text)]">Buckets</h2>
          <p className="text-[11px] text-[var(--co-muted)]">{INTRO}</p>
        </div>
        {(onAddRow || onSuggest) && (
          <div className="flex items-center gap-2">
            {onAddRow && (
              <Button variant="ghost" onClick={onAddRow} disabled={disabled} className={headerButton}>
                <Plus size={12} aria-hidden="true" />
                Add bucket
              </Button>
            )}
            {onSuggest && (
              <Button variant="ghost" onClick={onSuggest} disabled={disabled} className={headerButton}>
                <RefreshCw size={12} aria-hidden="true" />
                Suggest a ladder
              </Button>
            )}
          </div>
        )}
      </div>

      {!hasPriceEdge && (
        <p className="border-b border-[var(--co-border)] px-4 py-2 text-[11px] text-[var(--co-warning)]">
          No bucket starts at the price — one boundary must equal the whole-number price ({priceEdge}), or the
          validator will reject this ladder.
        </p>
      )}

      {floorsOverbooked && (
        <p className="border-b border-[var(--co-border)] px-4 py-2 text-[11px] text-[var(--co-error)]">
          Minimums add up to {Math.round(floorsTotal * 10) / 10}% — they must stay under 100%, otherwise there is
          nothing left for the solver to distribute.
        </p>
      )}

      {buckets && buckets.length > 0 && (
        <div className="border-b border-[var(--co-border)] px-4 py-3">
          <BucketShareBars groups={groups} buckets={buckets} price={price} />
        </div>
      )}

      <table aria-label="Buckets" className="w-full border-collapse">
        <thead>
          <tr className="border-b border-[var(--co-border)]">
            <th scope="col" className={`${head} w-10 text-left`}>
              #
            </th>
            <th scope="col" className={`${head} text-left`}>
              Range
            </th>
            <th scope="col" className={`${head} text-left`}>
              Min %
            </th>
            {preview && (
              <>
                <th scope="col" className={`${head} text-right`}>
                  Avg value
                </th>
                <th scope="col" className={`${head} text-right`}>
                  Share of pulls
                </th>
                {preview.yourShare && (
                  <th scope="col" className={`${head} text-right`}>
                    Your Normal share
                  </th>
                )}
              </>
            )}
            {onRemoveRow && (
              <th scope="col" className="w-8">
                <span className="sr-only">Remove</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {groups.map((group, index) => {
            const bucket = buckets?.[index];
            const empty = bucket?.stocked === false;
            const inverted = group.to !== null && group.to <= group.from;
            // A ladder the console cannot edit may carry a gap; the row shows its own ceiling and flags the mismatch.
            const gap = index !== last && group.to !== groups[index + 1]?.from;
            return (
              <Fragment key={index}>
                {index === firstHigh && (
                  <tr className="border-b border-[var(--co-border)]">
                    <td
                      colSpan={columns}
                      className="px-3 py-0.5 text-[10px] font-medium tracking-wide text-[var(--co-success)]"
                    >
                      {`at or above the price ${formatDollars(price)}`}
                    </td>
                  </tr>
                )}
                <tr
                  className={`border-b border-[var(--co-border)] last:border-b-0 ${empty ? 'bg-[var(--co-bg)]' : ''}`}
                >
                  <td className={`${cell} font-mono text-[var(--co-muted)]`}>{index + 1}</td>
                  <td className={cell}>
                    <div className="flex items-center gap-2">
                      <NumberCell
                        aria-label={`Bucket ${index + 1} from`}
                        value={group.from}
                        min={0}
                        step="0.01"
                        disabled={disabled}
                        invalid={inverted}
                        onCommit={(v) => updateFrom(index, v)}
                      />
                      <span aria-hidden="true" className="text-[var(--co-muted)]">
                        –
                      </span>
                      {index === last ? (
                        <NumberCell
                          aria-label={`Bucket ${index + 1} to`}
                          value={group.to}
                          min={0}
                          step="0.01"
                          disabled={disabled}
                          invalid={inverted}
                          clearable
                          placeholder="no limit"
                          onCommit={updateLastTo}
                        />
                      ) : (
                        <span
                          className={`w-24 px-2 text-right font-mono text-[12px] ${inverted || gap ? 'text-[var(--co-error)]' : 'text-[var(--co-muted)]'}`}
                        >
                          <span className="sr-only">to </span>
                          {group.to ?? 'no limit'}
                        </span>
                      )}
                      {empty && (
                        <span className="rounded border border-[var(--co-border)] px-1 text-[10px] font-medium uppercase tracking-wide text-[var(--co-muted)]">
                          empty
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={cell}>
                    <NumberCell
                      aria-label={`Bucket ${index + 1} min share`}
                      value={group.floor}
                      min={0}
                      max={100}
                      step="0.1"
                      disabled={disabled}
                      invalid={floorsOverbooked && (group.floor ?? 0) > 0}
                      clearable
                      placeholder="none"
                      onCommit={(v) => updateFloor(index, v)}
                    />
                  </td>
                  {preview && (
                    <>
                      <td className={figure}>{bucket?.avg_fmv != null ? formatMoney(bucket.avg_fmv) : '—'}</td>
                      <td className={figure}>{empty ? '—' : formatPercent(bucket?.share, 1)}</td>
                      {preview.yourShare && (
                        <td className={figure}>{empty ? '—' : formatPercent(bucket?.your_share, 1)}</td>
                      )}
                    </>
                  )}
                  {onRemoveRow && (
                    <td className="px-1 py-1.5 text-right">
                      <button
                        type="button"
                        aria-label={`Remove bucket ${index + 1}`}
                        title={`Remove bucket ${index + 1}`}
                        onClick={() => onRemoveRow(index)}
                        disabled={disabled || groups.length <= 1}
                        className="rounded p-1 text-[var(--co-muted)] hover:bg-[var(--co-bg)] hover:text-[var(--co-error)] disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <X size={14} aria-hidden="true" />
                      </button>
                    </td>
                  )}
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
